import React, { useEffect, useState, useCallback } from "react";
import { Plus, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";

export default function Customers() {
  const { selectedBrand, brands } = useBrand();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", brand: "" });

  const load = useCallback(() => api.get("/customers", { params: { brand: selectedBrand } }).then((r) => setRows(r.data)), [selectedBrand]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    try { await api.post("/customers", form); toast.success("Customer added"); setOpen(false); setForm({ name: "", phone: "", email: "", address: "", brand: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${rows.length} customers · ${selectedBrand}`}
        actions={<button data-testid="add-customer-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> New Customer</button>} />
      <div className="p-8">
        {rows.length === 0 ? <EmptyState icon={UserRound} title="No customers" hint="Add customers or they'll appear from orders." /> : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-white/10 bg-[#0a0a0a]">{["ID", "Name", "Phone", "Email", "Brand", "Orders"].map((h) => <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-3 font-mono text-xs text-zinc-500">{c.customer_id}</td>
                    <td className="py-3 px-3 text-zinc-200">{c.name}</td>
                    <td className="py-3 px-3 text-zinc-400 font-mono text-xs">{c.phone}</td>
                    <td className="py-3 px-3 text-zinc-400">{c.email}</td>
                    <td className="py-3 px-3 text-zinc-400">{c.brand}</td>
                    <td className="py-3 px-3 font-mono text-zinc-300">{c.orders_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Customer</DialogTitle><DialogDescription className="sr-only">Add a customer record.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <input data-testid="customer-name-input" className={inputCls} placeholder="Name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              <input className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <select className={inputCls} value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}><option value="">Brand</option>{brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}</select>
            <input className={inputCls} placeholder="Address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            <button data-testid="customer-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors">Add Customer</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
